import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Bot } from "lucide-react";
import api from "@/lib/api";

/**
 * Floating NPD AI assistant. Read-only Q&A over the user's access-scoped costing data.
 * Mounted globally (in Layout) so it's available on every page.
 */
export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: "user"|"assistant", content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const history = messages.slice(-6);
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/assistant/ask", { message: text, history });
      setMessages((m) => [...m, { role: "assistant", content: r.data?.reply || "…" }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${e?.response?.data?.detail || "Assistant is unavailable right now."}` }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const SUGGESTIONS = [
    "How many costings are approved?",
    "Which parts are marked Won?",
    "List my prototype quotes",
    "Total pipeline value?",
  ];

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-orange-500 to-purple-600 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition"
          title="Ask the NPD Assistant"
          data-testid="assistant-launcher"
        >
          <Sparkles size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-2rem)] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="assistant-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-purple-600 text-white px-4 py-3 flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center"><Bot size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight">NPD Assistant</div>
              <div className="text-[10px] opacity-90">Advanced Anmol Metcomp Pvt Ltd</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="assistant-close"><X size={18} /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center text-slate-400 text-xs mt-6">
                <Sparkles size={22} className="mx-auto mb-2 text-purple-400" />
                <div className="font-medium text-slate-500">Ask about your NPD costing data</div>
                <div className="mt-3 flex flex-col gap-1.5 items-stretch">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => setInput(s)}
                      className="text-left text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-purple-50 text-slate-600">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs whitespace-pre-wrap break-words ${
                  m.role === "user" ? "bg-purple-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-slate-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-2 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Ask about your NPD data…"
                className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-purple-400 max-h-24"
                data-testid="assistant-input"
              />
              <button onClick={send} disabled={busy || !input.trim()}
                className="h-9 w-9 flex-shrink-0 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white flex items-center justify-center"
                data-testid="assistant-send">
                <Send size={15} />
              </button>
            </div>
            <div className="text-[9px] text-slate-400 mt-1 text-center">Read-only · restricted to your access · Advanced Anmol Metcomp Pvt Ltd</div>
          </div>
        </div>
      )}
    </>
  );
}
